import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useStateSelector } from '../../../../../store/selectors';
import { FetchDataRoom } from '../../../../../slices/marketplace/data-room/thunks/fetch-data-room.thunk';
import DataRoom from '../../../../ConnectMarketplaceDashboard/DataRoom/DataRoom';
import { SourceType } from '../../../../../slices/marketplace/data-room/models/vdr-access-permission.model';
import { TeamMember } from '../../MyTeamMembers/Models/TeamMember';
import {
    InternalTeamMemberRoleOptionList,
    ExternalTeamMemberRoleOptionList,
    TeamMemberType,
    TeamMemberTypeOptionList,
} from '../../MyTeamMembers/Models/Enums';
import createNotification from '../../../../../utils/createNotification';
import { DataRoomTeamMember as DataRoomTeamMember } from '../../../../../components/DataRoomAccessRightsPopup/DataRoomAccessRightsPopup';

const OpportunityDataRoomHost: React.FC = () => {
    const dispatch = useDispatch() as any;
    const [searchParams] = useSearchParams();
    const opportunityIdNum = Number(searchParams.get('opportunityId'));

    const [membersWithAccessToOpportunity, setMembersWithAccessToOpportunity] =
        useState<TeamMember[]>([]);

    const axios = useStateSelector((state) => state.core.axios);
    const rootFolder = useStateSelector(
        (state) => state.dataRoomState.rootFolder
    );

    const selectedOpportunity = useStateSelector(
        (state) => state.opportunities.selectedOpportuity
    );
    const buyer = useStateSelector((state) => state.buyersState.targetBuyer);

    const canUserManagePermissions = selectedOpportunity?.isPrimaryBuyer ?? false;

    const fetchDataRoom = useCallback(() => {
        if (opportunityIdNum && !isNaN(opportunityIdNum)) {
            dispatch(
                FetchDataRoom({
                    sourceId: opportunityIdNum,
                    sourceType: SourceType.Engagement,
                })
            );
        }
    }, [dispatch, opportunityIdNum]);

    useEffect(() => {
        if (opportunityIdNum && !isNaN(opportunityIdNum)) {
            fetchDataRoom();
            fetchTeamMembers();
        }
    }, [dispatch, opportunityIdNum]);

    const fetchTeamMembers = async () => {
        axios
            .get(`/api/TeamMembers`)
            .then((response) => {
                const allMembers: TeamMember[] = response.data;
                const membersWithOpportunityAccess = allMembers.filter(
                    (member) =>
                        member.associateMemberMarketplaceMappings.some(
                            (x) => x.opportunityId === opportunityIdNum
                        )
                );

                setMembersWithAccessToOpportunity(membersWithOpportunityAccess);
            })
            .catch((error) => {
                createNotification(error.message, 'error');
            });
    };

    const transformedTeamMembers = useMemo((): DataRoomTeamMember[] => {
        return membersWithAccessToOpportunity.map((member) => {
            const mapping = member.associateMemberMarketplaceMappings.find(
                (x) => x.opportunityId === opportunityIdNum
            );

            let role = 'Team Member';
            let memberType = '';
            
            if (mapping) {
                memberType = TeamMemberTypeOptionList.find(
                    (t) => t.value === mapping.type
                )?.label || '';
                
                if (mapping.type === TeamMemberType.Internal) {
                    role =
                        InternalTeamMemberRoleOptionList.find(
                            (r) => r.value === mapping.internalRole
                        )?.label || 'Internal Team Member';
                } else if (mapping.type === TeamMemberType.External) {
                    role =
                        ExternalTeamMemberRoleOptionList.find(
                            (r) => r.value === mapping.externalRole
                        )?.label || 'External Team Member';
                }
            }

            return {
                id: member.id,
                userId: member.userId ?? 0,
                memberId: member.id || 0,
                name: member.name,
                memberType: memberType,
                role: role,
                email: member.email,
            };
        });
    }, [membersWithAccessToOpportunity, opportunityIdNum]);

    const handlePermissionsUpdated = useCallback(() => {
        fetchDataRoom();
    }, [fetchDataRoom]);


    return (
        <DataRoom
            engagementId={opportunityIdNum}
            rootFolder={rootFolder}
            practices={selectedOpportunity.practices}
            teamMembers={transformedTeamMembers}
            memberOwnerId={buyer?.userId ?? 0}
            onPermissionsUpdated={handlePermissionsUpdated}
            canUserManagePermissions={canUserManagePermissions}
        />
    );
};

export default OpportunityDataRoomHost;
